/**
 * Survey Vote Web Component
 * Handles vote form display, localStorage anti-duplicate, and success parameter detection
 * 
 * Architecture:
 * - 2 formulaires génériques Shopify Forms ("Vote option A" et "Vote option B")
 * - Le week_number est injecté dans les formulaires via un champ caché
 * - Après soumission, redirection avec ?success=1&choice=option_a
 * - localStorage stocke le choix pour éviter les doublons
 */

class SurveyVote extends HTMLElement {
  constructor() {
    super();
    this.week = '';
    this.status = '';
    this.pctA = 50;
    this.pctB = 50;
    this.countA = 0;
    this.countB = 0;
    this.labelA = '';
    this.labelB = '';
    this.voteLabelSingular = '';
    this.voteLabelPlural = '';
    this.totalVotesSuffix = '';
    this.votedChoice = null;
    this._submitHandlerBound = false;
    this._formsObserver = null;
    this._storageKey = '';
    this.votedMessageText = '';
    this._domSuccessDebugLogged = false;
  }

  connectedCallback() {
    this.week = this.dataset.week || '0';
    this.status = this.dataset.status || 'active';
    this.pctA = parseFloat(this.dataset.pctA || '50') || 50;
    this.pctB = parseFloat(this.dataset.pctB || '50') || 50;
    const countARaw = String(this.dataset.countA ?? '0');
    const countBRaw = String(this.dataset.countB ?? '0');
    const labelARaw = String(this.dataset.labelA ?? '');
    const labelBRaw = String(this.dataset.labelB ?? '');
    this.voteLabelSingular = String(this.dataset.voteLabelSingular ?? '');
    this.voteLabelPlural = String(this.dataset.voteLabelPlural ?? '');
    this.totalVotesSuffix = String(this.dataset.totalVotesSuffix ?? '');
    this.votedMessageText = String(this.dataset.votedMessageText ?? '');

    this.countA = parseInt(countARaw, 10) || 0;
    this.countB = parseInt(countBRaw, 10) || 0;
    this.labelA = labelARaw;
    this.labelB = labelBRaw;

    this._storageKey = `survey_voted_week_${this.week}`;

    console.log('🗳️ Survey Vote initialized for week', this.week);
    console.log('📊 Current results:', { pctA: this.pctA, pctB: this.pctB, countA: this.countA, countB: this.countB });

    // Les formulaires @app peuvent être injectés après le chargement initial.
    // On attache donc un seul handler "submit" au niveau du composant (capture),
    // puis on injecte/complète les champs cachés quand les formulaires apparaissent.
    this.bindSubmitHandlerOnce();

    this.injectWeekNumberInForms();

    // Source of truth: localStorage (anti-doublon UX).
    // On garde la lecture optionnelle des params pour compatibilité, mais on évite de double-incrémenter.
    this.votedChoice = localStorage.getItem(this._storageKey);

    const params = new URLSearchParams(window.location.search);
    const successChoice = params.get('choice');
    if (params.get('success') === '1' && successChoice && !this.votedChoice) {
      console.log('✅ Vote success detected (fallback params). Choice:', successChoice);
      localStorage.setItem(this._storageKey, successChoice);
      this.votedChoice = successChoice;
    }

    if (this.status === 'active') {
      this.syncVisibility();
    }

    this.observeFormsForLateInjection();
  }

  bindSubmitHandlerOnce() {
    if (this._submitHandlerBound) return;
    this._submitHandlerBound = true;

    // Event "submit" peut ne pas bubble suivant l'implémentation.
    // En capture, on récupère l'évènement même si Shopify rend les formulaires après coup.
    this.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const formWrapper = form.closest('[data-option]');
        const actualOptionRaw = formWrapper instanceof HTMLElement ? formWrapper.dataset.option : undefined;
        const actualOption =
          actualOptionRaw === 'option_a' || actualOptionRaw === 'option_b' ? actualOptionRaw : null;

        if (!actualOption) return;
        if (this.status !== 'active') return;

        // Anti-doublon UX: on marque le vote immédiatement, sans attendre une redirection.
        localStorage.setItem(this._storageKey, actualOption);
        this.votedChoice = actualOption;

        // Mise à jour optimiste côté UI (si la soumission reste sur la même page).
        this.incrementOptimistic(actualOption);
        this.syncVisibility();
      },
      true
    );
  }

  injectWeekNumberInForms() {
    const forms = this.querySelectorAll('form');
    forms.forEach((form) => {
      const existingWeekInput = form.querySelector('input[name="week_number"]');
      if (!existingWeekInput) {
        const weekInput = document.createElement('input');
        weekInput.type = 'hidden';
        weekInput.name = 'week_number';
        weekInput.value = String(this.week);
        form.appendChild(weekInput);
      }
    });
  }

  observeFormsForLateInjection() {
    if (this._formsObserver) return;

    this._formsObserver = new MutationObserver(() => {
      // On resynchronise les champs cachés dès que Shopify/Apps ajoutent du DOM.
      this.injectWeekNumberInForms();
      // Et on re-force le masquage/affichage, car Shopify Forms peut réinjecter
      // son UI de succès et redéfinir des styles.
      this.syncVisibility();
      this.detectDomSuccessState();
    });
    this._formsObserver.observe(this, { childList: true, subtree: true });
  }

  detectDomSuccessState() {
    if (this.status !== 'active') return;
    if (this.votedChoice) return;

    const votedText = this.votedMessageText.trim();
    if (!votedText) return;

    /** @param {unknown} s */
    const normalize = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();
    const normalizedVotedText = normalize(votedText);

    // Heuristique: Shopify Forms “Afficher le message” remplace le contenu du form
    // par un bloc de confirmation contenant le texte choisi (ex: "Merci pour ton vote !").
    const optionAEl = this.querySelector('.survey-vote__option-form[data-option="option_a"]');
    const optionBEl = this.querySelector('.survey-vote__option-form[data-option="option_b"]');

    const optionAText = optionAEl instanceof HTMLElement ? optionAEl.textContent || '' : '';
    const optionBText = optionBEl instanceof HTMLElement ? optionBEl.textContent || '' : '';

    if (!this._domSuccessDebugLogged) {
      this._domSuccessDebugLogged = true;
      console.log('🧪 Survey Vote DOM debug', {
        week: this.week,
        votedText: normalizedVotedText,
        optionATextSample: normalize(optionAText).slice(0, 120),
        optionBTextSample: normalize(optionBText).slice(0, 120),
        optionATextHasForm: optionAEl ? !!optionAEl.querySelector('form') : null,
        optionBTextHasForm: optionBEl ? !!optionBEl.querySelector('form') : null,
      });
    }

    const normalizedOptionAText = normalize(optionAText);
    const normalizedOptionBText = normalize(optionBText);

    if (normalizedOptionAText.includes(normalizedVotedText)) {
      this.votedChoice = 'option_a';
      console.log('🧠 DOM success detected: option_a');
      localStorage.setItem(this._storageKey, this.votedChoice);
      this.incrementOptimistic(this.votedChoice);
      this.syncVisibility();
    } else if (normalizedOptionBText.includes(normalizedVotedText)) {
      this.votedChoice = 'option_b';
      console.log('🧠 DOM success detected: option_b');
      localStorage.setItem(this._storageKey, this.votedChoice);
      this.incrementOptimistic(this.votedChoice);
      this.syncVisibility();
    }
  }

  /**
   * @param {string} choice
   * @returns {void}
   */
  incrementOptimistic(choice) {
    if (choice === 'option_a') {
      this.countA += 1;
    } else if (choice === 'option_b') {
      this.countB += 1;
    }

    const total = this.countA + this.countB;
    if (total > 0) {
      this.pctA = Math.round((this.countA / total) * 100);
      this.pctB = 100 - this.pctA;
    }

    console.log('📈 Optimistic update:', { pctA: this.pctA, pctB: this.pctB });
    
    this.updateResultsDisplay();
  }

  showVoteForm() {
    this.votedChoice = null;
    this.syncVisibility();
  }

  showResults() {
    if (!this.votedChoice) return;
    this.syncVisibility();
  }

  syncVisibility() {
    if (this.status !== 'active') return;

    const formContainerEl = this.querySelector(
      '.survey-vote__form-container[data-show-if="not-voted"]'
    );
    const resultsContainerEl = this.querySelector(
      '.survey-vote__results-container[data-show-if="voted"]'
    );

    const hasVoted = !!this.votedChoice;

    if (formContainerEl instanceof HTMLElement) {
      formContainerEl.style.display = hasVoted ? 'none' : '';
    }
    if (resultsContainerEl instanceof HTMLElement) {
      resultsContainerEl.style.display = hasVoted ? '' : 'none';
    }

    if (hasVoted) {
      const choiceLabel = this.querySelector('.survey-vote__choice-label');
      if (choiceLabel) {
        choiceLabel.textContent = this.votedChoice === 'option_a' ? this.labelA : this.labelB;
      }
      this.updateResultsDisplay();
    }
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  formatCount(count) {
    const unit = count > 1 ? this.voteLabelPlural : this.voteLabelSingular;
    const suffix = this.totalVotesSuffix ? ` ${this.totalVotesSuffix}` : '';
    return unit ? `${count} ${unit}${suffix}` : `${count}${suffix}`;
  }

  updateResultsDisplay() {
    // On met à jour TOUTES les barres présentes dans le composant
    // (live bar avant vote, voted bar après vote, final bar pour les sondages clos).
    const bars = this.querySelectorAll('.survey-results-bar');
    bars.forEach((bar) => {
      if (!(bar instanceof HTMLElement)) return;
      const format = bar.dataset.format === 'counts' ? 'counts' : 'percent';

      const fillA = bar.querySelector('.survey-results-bar__fill--a');
      const fillB = bar.querySelector('.survey-results-bar__fill--b');
      const pctA = bar.querySelector('.survey-results-bar__pct--a');
      const pctB = bar.querySelector('.survey-results-bar__pct--b');
      const totalEl = bar.querySelector('.survey-results-bar__total');

      if (fillA instanceof HTMLElement) fillA.style.width = `${this.pctA}%`;
      if (fillB instanceof HTMLElement) fillB.style.width = `${this.pctB}%`;

      if (format === 'counts') {
        if (pctA instanceof HTMLElement) pctA.textContent = this.formatCount(this.countA);
        if (pctB instanceof HTMLElement) pctB.textContent = this.formatCount(this.countB);
      } else {
        if (pctA instanceof HTMLElement) pctA.textContent = `${this.pctA}%`;
        if (pctB instanceof HTMLElement) pctB.textContent = `${this.pctB}%`;
      }

      const total = this.countA + this.countB;
      if (totalEl instanceof HTMLElement && total > 0) {
        const unit = total > 1 ? this.voteLabelPlural : this.voteLabelSingular;
        totalEl.textContent = `${total} ${unit}${this.totalVotesSuffix ? ` ${this.totalVotesSuffix}` : ''}`;
      }
    });
  }
}

customElements.define('survey-vote', SurveyVote);


/**
 * Survey Current Web Component
 * Handles the current survey display on the list page
 */
class SurveyCurrent extends HTMLElement {
  constructor() {
    super();
    this.week = null;
  }

  connectedCallback() {
    this.week = this.dataset.week || '0';
    const storageKey = `survey_voted_week_${this.week}`;
    const votedChoice = localStorage.getItem(storageKey);
    const hasVoted = !!votedChoice;
    const labelA = String(this.dataset.labelA ?? '');
    const labelB = String(this.dataset.labelB ?? '');

    console.log('📊 Survey Current - Week:', this.week, '| Has voted:', !!hasVoted);

    const voteCta = this.querySelector('[data-show-if="not-voted"]');
    const votedMessage = this.querySelector('[data-show-if="voted"]');
    // Type guard: `querySelector` renvoie `Element | null` mais on accède à `.style`
    const voteCtaEl = voteCta instanceof HTMLElement ? voteCta : null;
    const votedMessageEl = votedMessage instanceof HTMLElement ? votedMessage : null;

    if (hasVoted) {
      if (voteCtaEl) voteCtaEl.style.display = 'none';
      if (votedMessageEl) votedMessageEl.style.display = '';

      const choiceLabel = this.querySelector('.survey-current__voted-choice-label');
      if (choiceLabel && votedChoice) {
        choiceLabel.textContent = votedChoice === 'option_a' ? labelA : labelB;
      }
    } else {
      if (voteCtaEl) voteCtaEl.style.display = '';
      if (votedMessageEl) votedMessageEl.style.display = 'none';
    }
  }
}

customElements.define('survey-current', SurveyCurrent);
